import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { ButtonHTMLAttributes, FormEvent, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import {
  SalesProductAzureLink,
  SalesProductBacklogItem,
  SalesProductBacklogItemUpsertPayload,
  SalesProductLinkedApplication,
  createSalesProductBacklogItem,
  deleteSalesProductBacklogAzureLink,
  deleteSalesProductBacklogItem,
  linkSalesProductBacklogAzure,
  linkSalesProductBacklogRequirement,
  listSalesProductBacklog,
  listSalesProductBacklogRequirements,
  listSalesProductLinkedApplications,
  listSalesProductRequirements,
  unlinkSalesProductBacklogRequirement,
  refreshSalesProductBacklogAzureLink,
  updateSalesProductBacklogItem,
} from '../../services/salesProductWorkspaceApi';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';
import { DeleteIconButton } from '../ui/DeleteIconButton';
import { DraggableModalPanel } from '../ui/DraggableModalPanel';
import { EditIconButton } from '../ui/EditIconButton';
import { FormActionBar } from '../ui/FormActionBar';
import { ListPaginationBar } from '../ui/ListPaginationBar';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { RequiredMark } from '../ui/RequiredMark';
import { RelatedRequirementsSection } from '../requirements/RelatedRequirementsSection';
import { ProductAzureLinksPanel } from './ProductAzureLinksPanel';

const PAGE_SIZE = 10;

const PRIORITY_OPTIONS = [
  { value: 'Low', label: 'Baixa' },
  { value: 'Medium', label: 'Média' },
  { value: 'High', label: 'Alta' },
  { value: 'Critical', label: 'Crítica' },
];

const STATUS_OPTIONS = [
  { value: 'Idea', label: 'Ideia' },
  { value: 'Ready', label: 'Pronto' },
  { value: 'InProgress', label: 'Em andamento' },
  { value: 'Done', label: 'Concluído' },
  { value: 'Cancelled', label: 'Cancelado' },
];

const AZURE_KIND_LABELS: Record<string, string> = {
  WorkItem: 'Work item',
  PullRequest: 'Pull request',
};

type ProductBacklogPanelProps = {
  productId: string;
  canWrite: boolean;
};

function ActionIconButton({
  hint,
  children,
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { hint: string; children: ReactNode }) {
  return (
    <span title={hint} className="inline-flex">
      <button
        type="button"
        aria-label={hint}
        className={`inline-flex items-center justify-center rounded-md p-1.5 text-secondary-500 transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${className || 'hover:bg-secondary-100 hover:text-secondary-800'}`}
        {...props}
      >
        {children}
      </button>
    </span>
  );
}

function labelFor(options: { value: string; label: string }[], value: string): string {
  return options.find((option) => option.value === value)?.label ?? value;
}

function toDateInput(value?: string | null): string {
  return value ? value.slice(0, 10) : '';
}

function formatDeliveryDate(value?: string | null): string {
  const date = toDateInput(value);
  if (!date) return '';
  const [year, month, day] = date.split('-');
  return `${day}/${month}/${year}`;
}

function emptyForm(): SalesProductBacklogItemUpsertPayload {
  return { title: '', priority: 'Medium', status: 'Idea', sortOrder: 0 };
}

function formFromItem(item: SalesProductBacklogItem): SalesProductBacklogItemUpsertPayload {
  return {
    title: item.title,
    description: item.description ?? '',
    priority: item.priority,
    status: item.status,
    plannedDeliveryDateUtc: item.plannedDeliveryDateUtc,
    estimatePoints: item.estimatePoints,
    estimateHours: item.estimateHours,
    sortOrder: item.sortOrder,
    tags: item.tags ?? '',
  };
}

export function ProductBacklogPanel({ productId, canWrite }: ProductBacklogPanelProps) {
  const { confirm } = useConfirmDialog();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState<SalesProductBacklogItem[]>([]);
  const [applications, setApplications] = useState<SalesProductLinkedApplication[]>([]);
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [azureModalOpen, setAzureModalOpen] = useState(false);

  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const pageItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return items.slice(start, start + PAGE_SIZE);
  }, [items, page]);
  const selected = useMemo(
    () => items.find((item) => item.id === selectedId) ?? null,
    [items, selectedId],
  );

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const [nextItems, nextApplications] = await Promise.all([
        listSalesProductBacklog(productId),
        listSalesProductLinkedApplications(productId),
      ]);
      setItems(nextItems);
      setApplications(nextApplications);
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao carregar o backlog.'));
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    setPage(1);
    setSelectedId(null);
    setFormOpen(false);
    setAzureModalOpen(false);
    void loadItems();
  }, [loadItems]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  useEffect(() => {
    if (pageItems.length === 0) {
      if (selectedId) setSelectedId(null);
      return;
    }
    if (!selectedId || !pageItems.some((item) => item.id === selectedId)) {
      setSelectedId(pageItems[0].id);
    }
  }, [pageItems, selectedId]);

  function openCreate(): void {
    setEditingId(null);
    setForm(emptyForm());
    setFormOpen(true);
  }

  function openEdit(item: SalesProductBacklogItem): void {
    setEditingId(item.id);
    setForm(formFromItem(item));
    setFormOpen(true);
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setSaving(true);
    try {
      const saved = editingId
        ? await updateSalesProductBacklogItem(productId, editingId, form)
        : await createSalesProductBacklogItem(productId, form);
      toast.success(editingId ? 'Item atualizado.' : 'Item criado.');
      setFormOpen(false);
      const nextItems = await listSalesProductBacklog(productId);
      setItems(nextItems);
      const index = nextItems.findIndex((item) => item.id === saved.id);
      if (index >= 0) {
        setPage(Math.floor(index / PAGE_SIZE) + 1);
      }
      setSelectedId(saved.id);
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao salvar o item.'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item: SalesProductBacklogItem): Promise<void> {
    const confirmed = await confirm({
      title: 'Excluir item',
      message: `Excluir ${item.title}?`,
      confirmLabel: 'Excluir',
      tone: 'danger',
    });
    if (!confirmed) return;
    try {
      await deleteSalesProductBacklogItem(productId, item.id);
      toast.success('Item excluído.');
      if (selectedId === item.id) setSelectedId(null);
      await loadItems();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao excluir o item.'));
    }
  }

  async function reloadKeepingSelection(): Promise<void> {
    await loadItems();
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
      <section className="rounded-xl border border-secondary-200 bg-white">
        <div className="flex items-center justify-between gap-3 border-b border-secondary-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-secondary-900">Backlog</h2>
          {canWrite ? (
            <button type="button" className="erp-btn-primary" onClick={openCreate}>
              Novo item
            </button>
          ) : null}
        </div>
        {loading ? (
          <LoadingSpinner label="Carregando..." />
        ) : items.length === 0 ? (
          <p className="px-4 py-6 text-sm text-secondary-500">Nenhum item de backlog.</p>
        ) : (
          <ul className="divide-y divide-secondary-100">
            {pageItems.map((item) => {
              const active = item.id === selectedId;
              return (
                <li key={item.id} className={active ? 'bg-primary-50' : 'hover:bg-secondary-50'}>
                  <div className="flex items-start gap-2 px-3 py-3">
                    <button
                      type="button"
                      className="flex min-w-0 flex-1 flex-col gap-1 text-left"
                      onClick={() => setSelectedId(item.id)}
                    >
                      <span className="font-medium text-secondary-900">{item.title}</span>
                      <span className="text-xs text-secondary-500">
                        {labelFor(PRIORITY_OPTIONS, item.priority)} · {labelFor(STATUS_OPTIONS, item.status)}
                        {item.plannedDeliveryDateUtc
                          ? ` · Previsão ${formatDeliveryDate(item.plannedDeliveryDateUtc)}`
                          : ''}
                        {(item.azureLinks?.length ?? 0) > 0
                          ? ` · ${item.azureLinks.length} no Azure`
                          : ''}
                      </span>
                    </button>
                    {canWrite ? (
                      <div className="flex shrink-0">
                        <EditIconButton label="Editar item" onClick={() => openEdit(item)} />
                        <DeleteIconButton label="Excluir item" onClick={() => void handleDelete(item)} />
                      </div>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        <div className="border-t border-secondary-100 px-2">
          <ListPaginationBar
            totalCount={items.length}
            page={page}
            pageSize={PAGE_SIZE}
            onPrevious={() => setPage((current) => Math.max(1, current - 1))}
            onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
          />
        </div>
      </section>

      <section className="rounded-xl border border-secondary-200 bg-white px-4 py-4">
        {!selected ? (
          <p className="text-sm text-secondary-500">Selecione um item para ver o detalhe.</p>
        ) : (
          <BacklogDetail
            productId={productId}
            canWrite={canWrite}
            item={selected}
            onOpenAzure={() => setAzureModalOpen(true)}
          />
        )}
      </section>

      <DraggableModalPanel
        open={formOpen}
        title={editingId ? 'Editar item de backlog' : 'Novo item de backlog'}
        onClose={() => setFormOpen(false)}
        closeDisabled={saving}
        className="w-full max-w-3xl"
        footer={
          <FormActionBar onCancel={() => setFormOpen(false)} cancelLabel="Cancelar">
            <button type="submit" form="product-backlog-form" className="erp-btn-primary" disabled={saving}>
              {saving ? 'Salvando...' : editingId ? 'Atualizar' : 'Criar'}
            </button>
          </FormActionBar>
        }
      >
        <form id="product-backlog-form" className="grid gap-3 md:grid-cols-4" onSubmit={(event) => void handleSubmit(event)}>
          <label className="erp-label md:col-span-2">
            Título <RequiredMark />
            <input
              className="erp-input mt-1"
              required
              value={form.title}
              onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
            />
          </label>
          <label className="erp-label">
            Prioridade
            <select
              className="erp-input mt-1"
              value={form.priority}
              onChange={(event) => setForm((current) => ({ ...current, priority: event.target.value }))}
            >
              {PRIORITY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="erp-label">
            Status
            <select
              className="erp-input mt-1"
              value={form.status}
              onChange={(event) => setForm((current) => ({ ...current, status: event.target.value }))}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="erp-label">
            Previsão de entrega
            <input
              className="erp-input mt-1"
              type="date"
              value={toDateInput(form.plannedDeliveryDateUtc)}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  plannedDeliveryDateUtc: event.target.value ? `${event.target.value}T00:00:00.000Z` : null,
                }))
              }
            />
          </label>
          <label className="erp-label md:col-span-4">
            Descrição
            <textarea
              className="erp-input mt-1"
              rows={3}
              value={form.description ?? ''}
              onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
            />
          </label>
        </form>
      </DraggableModalPanel>

      {selected ? (
        <DraggableModalPanel
          open={azureModalOpen}
          title="Azure DevOps"
          subtitle={selected.title}
          onClose={() => setAzureModalOpen(false)}
          className="w-full max-w-3xl"
        >
          <ProductAzureLinksPanel
            applications={applications}
            links={selected.azureLinks ?? []}
            canWrite={canWrite}
            onLink={async (payload) => {
              await linkSalesProductBacklogAzure(productId, selected.id, payload);
              await reloadKeepingSelection();
            }}
            onRefresh={async (linkId) => {
              await refreshSalesProductBacklogAzureLink(productId, selected.id, linkId);
              toast.success('Vínculo atualizado.');
              await reloadKeepingSelection();
            }}
            onDelete={async (linkId) => {
              await deleteSalesProductBacklogAzureLink(productId, selected.id, linkId);
              toast.success('Vínculo removido.');
              await reloadKeepingSelection();
            }}
          />
        </DraggableModalPanel>
      ) : null}
    </div>
  );
}

function BacklogDetail({
  productId,
  canWrite,
  item,
  onOpenAzure,
}: {
  productId: string;
  canWrite: boolean;
  item: SalesProductBacklogItem;
  onOpenAzure: () => void;
}) {
  const links = item.azureLinks ?? [];

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold text-secondary-900">{item.title}</h2>
        <p className="mt-1 text-sm text-secondary-600">
          {labelFor(PRIORITY_OPTIONS, item.priority)} · {labelFor(STATUS_OPTIONS, item.status)}
        </p>
      </div>

      {item.description ? (
        <p className="whitespace-pre-wrap text-sm text-secondary-700">{item.description}</p>
      ) : null}

      <dl className="grid gap-3 text-sm sm:grid-cols-2">
        <div>
          <dt className="text-secondary-500">Prioridade</dt>
          <dd className="font-medium text-secondary-900">{labelFor(PRIORITY_OPTIONS, item.priority)}</dd>
        </div>
        <div>
          <dt className="text-secondary-500">Status</dt>
          <dd className="font-medium text-secondary-900">{labelFor(STATUS_OPTIONS, item.status)}</dd>
        </div>
        <div>
          <dt className="text-secondary-500">Previsão de entrega</dt>
          <dd className="font-medium text-secondary-900">
            {formatDeliveryDate(item.plannedDeliveryDateUtc) || '—'}
          </dd>
        </div>
        {item.tags ? (
          <div className="sm:col-span-2">
            <dt className="text-secondary-500">Tags</dt>
            <dd className="font-medium text-secondary-900">{item.tags}</dd>
          </div>
        ) : null}
      </dl>

      <RelatedRequirementsSection
        sourceKey={item.id}
        canWrite={canWrite}
        emptyCatalogMessage="Nenhum requisito deste produto. Cadastre na aba Requisitos e volte para associar."
        loadCatalog={() => listSalesProductRequirements(productId)}
        loadLinked={() => listSalesProductBacklogRequirements(productId, item.id)}
        onLink={(requirementId) =>
          linkSalesProductBacklogRequirement(productId, item.id, requirementId).then(() => undefined)
        }
        onUnlink={(requirementId) => unlinkSalesProductBacklogRequirement(productId, item.id, requirementId)}
      />

      <div>
        <div className="mb-2 flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-secondary-900">Azure DevOps</h3>
          <ActionIconButton hint="Abre a lista completa dos itens do Azure." onClick={onOpenAzure}>
            <ArrowTopRightOnSquareIcon className="h-5 w-5" />
          </ActionIconButton>
        </div>
        {links.length === 0 ? (
          <p className="text-sm text-secondary-500">Nenhum work item ou pull request vinculado.</p>
        ) : (
          <ul className="divide-y divide-secondary-100 rounded-lg border border-secondary-100">
            {links.map((link) => (
              <AzureLinkRow key={link.id} link={link} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function AzureLinkRow({ link }: { link: SalesProductAzureLink }) {
  const kind = AZURE_KIND_LABELS[link.kind] ?? link.kind;
  return (
    <li className="px-3 py-2 text-sm">
      <p className="font-medium text-secondary-900">
        {kind} {link.externalId}
        {link.applicationName ? ` · ${link.applicationName}` : ''}
      </p>
      <p className="text-secondary-700">{link.title}</p>
      <p className="text-xs text-secondary-500">{link.state || 'Sem estado'}</p>
    </li>
  );
}
